import { LoomActivePartActions } from "./LoomActivePartActions";
import { LoomAutoParameterControls } from "./LoomAutoParameterControls";
import { useLoomControlSurfaceContext } from "./LoomControlSurfaceContext";
import { LoomExplicitSurfaceCage } from "./LoomExplicitSurfaceCage";
import { LoomJunctionFairingEditor, type LoomJunctionFairingDraft } from "./LoomJunctionFairingEditor";
import type { LoomProductSectionDesign, LoomProductSelection } from "./LoomProductTree";
import { LoomTaskControlEditor } from "./LoomTaskControlEditor";
import { createLoomCommandId, type LoomCommandAcceptance } from "./loomAuthoringHistory";
import type { LoomAuthoringPreviewRequest } from "./useLoomAuthoringHistory";
import type { LoomAuthoringCommand, LoomAuthoringDocument } from "./loomAuthoringModel";

interface LoomTaskPropertiesPanelProps {
  readonly document: LoomAuthoringDocument;
  readonly onCommand: (command: LoomAuthoringCommand, acceptance?: LoomCommandAcceptance) => string | null;
  readonly onFairingDraftChange?: (draft: LoomJunctionFairingDraft) => void;
  readonly onOpenSection: () => void;
  readonly onPreviewCancel: () => void;
  readonly onPreviewCommand: (command: LoomAuthoringCommand, request: LoomAuthoringPreviewRequest) => string | null;
  readonly onSelect: (nodeId: string) => void;
  readonly onSelectFairing?: (nodeId: string, transitionId: string) => void;
  readonly sectionDesigns: readonly LoomProductSectionDesign[];
  readonly selection: LoomProductSelection;
}

export function LoomTaskPropertiesPanel({
  document, onCommand, onFairingDraftChange, onOpenSection, onPreviewCancel,
  onPreviewCommand, onSelect, onSelectFairing,
  sectionDesigns, selection,
}: LoomTaskPropertiesPanelProps) {
  const controls = useLoomControlSurfaceContext();
  const node = document.nodes.find((candidate) => candidate.id === selection.nodeId) ?? null;
  if (!node) return <aside aria-label="Properties" className="loom-native-task-properties pg-panel pg-inspector">
    <h2>Properties</h2><p>Select an aircraft component to edit it.</p>
  </aside>;
  if (selection.kind === "section") {
    const section = sectionDesigns.find((candidate) => candidate.id === selection.sectionId);
    return <aside aria-label="Properties" className="loom-native-task-properties pg-panel pg-inspector"
      data-selection-kind="section" data-testid="loom-task-properties">
      <header><span>Section design</span><h2>{section?.label ?? "Section"}</h2></header>
      <p>Source: {node.label}</p>
    </aside>;
  }
  if (selection.kind === "fairing") {
    const fairing = node.type === "primitive" && node.definition.primitive === "wing"
      && node.definition.junctionFairing?.transitionId === selection.transitionId
      ? node.definition.junctionFairing : null;
    return <aside aria-label="Properties" className="loom-native-task-properties pg-panel pg-inspector"
      data-selection-kind="fairing" data-testid="loom-task-properties">
      <header><span>Junction fairing</span><h2>Wing–body fairing</h2></header>
      <p>Linked to {node.label}</p>
      {fairing && node.type === "primitive" ? <LoomJunctionFairingEditor document={document}
        node={node} onCommand={onCommand} onDraftChange={onFairingDraftChange} />
        : <p>The linked fairing is unavailable.</p>}
    </aside>;
  }
  if (selection.kind === "control") {
    const control = controls?.selectedControl?.id === selection.controlSurfaceId
      ? controls.selectedControl : null;
    return <aside aria-label="Properties" className="loom-native-task-properties pg-panel pg-inspector"
      data-selection-kind="control" data-testid="loom-task-properties">
      <header><span>Control surface</span><h2>{control?.label ?? "Control surface"}</h2></header>
      <div className="loom-task-primary-actions"><button onClick={onOpenSection}
        type="button">New section</button></div>
      {control ? <label className="loom-task-field"><span>Name</span><input aria-label="Name"
        defaultValue={control.label} key={`${control.id}:${control.label}`}
        onBlur={(event) => {
          const label = event.target.value.trim();
          if (label && label !== control.label) controls?.replaceControlSurface({ ...control, label });
        }} /></label> : null}
      <LoomTaskControlEditor />
    </aside>;
  }
  const rename = (label: string) => {
    if (!label.trim() || label === node.label) return;
    onCommand({
      schema: "loom-authoring-command/v1",
      commandId: createLoomCommandId("task-rename", document.revision),
      expectedRevision: document.revision,
      kind: "node.rename", nodeId: node.id, label: label.trim(),
    }, { origin: "gui.scene", subjectIds: [node.id] });
  };
  const junctionFairing = node.type === "primitive" && node.definition.primitive === "wing"
    ? node.definition.junctionFairing : undefined;
  return <aside aria-label="Properties" className="loom-native-task-properties pg-panel pg-inspector"
    data-selection-kind="node" data-testid="loom-task-properties">
    <header><span>{node.type === "primitive" ? node.definition.primitive : node.type}</span>
      <h2>{node.label}</h2></header>
    <div className="loom-task-primary-actions">
      <button onClick={onOpenSection} type="button">New section</button>
    </div>
    <label className="loom-task-field"><span>Name</span><input aria-label="Name"
      defaultValue={node.label} key={`${node.id}:${node.label}`}
      onBlur={(event) => rename(event.target.value)} /></label>
    <LoomTaskControlEditor />
    {node.type === "primitive" && node.definition.primitive === "wing"
      ? <LoomJunctionFairingEditor document={document} mode="summary" node={node}
        onCommand={onCommand} onEdit={junctionFairing && onSelectFairing
          ? () => onSelectFairing(node.id, junctionFairing.transitionId) : undefined} /> : null}
    <LoomAutoParameterControls document={document} nodeId={node.id} onCommand={onCommand}
      onPreviewCancel={onPreviewCancel} onPreviewCommand={onPreviewCommand} pinnedControlIds={[]}
      taskGroups={node.type === "primitive" && node.definition.primitive === "wing"} />
    <details className="loom-task-technical" data-technical-details>
      <summary>Advanced geometry</summary>
      <LoomActivePartActions document={document} node={node} onCommand={onCommand} onSelect={onSelect} />
      <LoomExplicitSurfaceCage document={document} nodeId={node.id} onCommand={onCommand}
        onPreviewCancel={onPreviewCancel} onPreviewCommand={onPreviewCommand} />
    </details>
  </aside>;
}
